import React, { useEffect, useRef, useState } from "react";

const FAMILY_MEDICAL_HISTORY_OPTIONS = [
  "Hypertension",
  "High cholesterol",
  "Cardiac issues",
  "Cancer",
  "Diabetes",
];

const FamilyMedicalHistorySection = ({ values, onChange }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedTitles = FAMILY_MEDICAL_HISTORY_OPTIONS.filter((option) =>
    values.includes(option),
  ).join(", ");

  const toggleOption = (optionValue, isChecked) => {
    const nextSelection = isChecked
      ? [...values, optionValue]
      : values.filter((value) => value !== optionValue);

    onChange(nextSelection);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  return (
    <div className="relative mt-4 z-20 px-4">
      <label
        className="block font-fatfrank text-sas-primary md:text-lg font-bold"
        htmlFor="family-medical-history"
      >
        Family Medical History
      </label>
      <div className="relative mt-2" ref={dropdownRef}>
        <button
          type="button"
          id="family-medical-history"
          aria-haspopup="listbox"
          aria-expanded={isDropdownOpen}
          className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base font-semibold text-sas-primary-dark font-montserrat focus:outline-none focus:ring-2 focus:ring-sas-primary/30 bg-white cursor-pointer"
          onClick={() => setIsDropdownOpen((previous) => !previous)}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm md:text-base text-left font-semibold text-sas-primary-dark">
                {values.length > 0
                  ? `${values.length} selected`
                  : "Select one or more options"}
              </p>
              <p className="mt-0.5 text-xs text-slate-500 truncate text-left">
                {selectedTitles || "No family history selected"}
              </p>
            </div>
            <span
              aria-hidden="true"
              className="text-sas-primary text-sm leading-none pt-1"
            >
              {isDropdownOpen ? "▲" : "▼"}
            </span>
          </div>
        </button>

        {isDropdownOpen ? (
          <div
            role="listbox"
            aria-label="Family medical history options"
            className="absolute z-20 mt-2 w-full rounded-xl border border-slate-200 bg-white p-2 shadow-lg"
          >
            {FAMILY_MEDICAL_HISTORY_OPTIONS.map((option) => {
              const optionId = `family-history-${option
                .toLowerCase()
                .replace(/\s+/g, "-")}`;
              const checked = values.includes(option);

              return (
                <label
                  key={option}
                  htmlFor={optionId}
                  className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-sky-50 cursor-pointer"
                >
                  <input
                    id={optionId}
                    type="checkbox"
                    className="h-4 w-4 rounded border border-slate-300 text-sas-primary focus:ring-2 focus:ring-sas-primary/30"
                    checked={checked}
                    onChange={(e) => toggleOption(option, e.target.checked)}
                  />
                  <span className="text-sm font-semibold text-sas-primary-dark font-montserrat">
                    {option}
                  </span>
                </label>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default FamilyMedicalHistorySection;
